/**
 * Player Store - Manages multi-provider audio playback
 *
 * Supports:
 * - Spotify Web Playback SDK (premium users)
 * - Apple MusicKit JS
 * - Preview audio fallback (30-second clips)
 */

import { signal, computed } from '@preact/signals';
import type { SpotifyTrack, AppleMusicTrack, Song, SampleSong, MusicProvider, UnifiedTrack, MusicKitInstance } from '@/types';
import { providerConnections } from './auth';

// Spotify SDK types (loaded dynamically)
declare namespace Spotify {
  interface Player {
    connect(): Promise<boolean>;
    disconnect(): void;
    pause(): Promise<void>;
    resume(): Promise<void>;
    togglePlay(): Promise<void>;
    seek(position_ms: number): Promise<void>;
    nextTrack(): Promise<void>;
    previousTrack(): Promise<void>;
    setVolume(volume: number): Promise<void>;
    addListener(event: string, callback: (state: any) => void): void;
  }

  interface PlayerOptions {
    name: string;
    getOAuthToken: (cb: (token: string) => void) => void;
    volume?: number;
  }
}

// ============================================
// STATE
// ============================================

export const isReady = signal(false);
export const isPlaying = signal(false);
export const currentTrack = signal<SpotifyTrack | AppleMusicTrack | Song | SampleSong | UnifiedTrack | null>(null);
export const currentProvider = signal<MusicProvider | 'preview' | null>(null);
export const position = signal(0);
export const duration = signal(0);
export const volume = signal(0.5);

// Provider-specific state
export const spotifySdkLoaded = signal(false);
export const spotifyDeviceId = signal<string | null>(null);
export const appleMusicReady = signal(false);

// Preview audio element (for non-premium users or no provider)
let previewAudio: HTMLAudioElement | null = null;

// Spotify SDK player instance
let spotifyPlayer: Spotify.Player | null = null;

// MusicKit instance
let musicKit: MusicKitInstance | null = null;

// Position update interval
let positionInterval: number | null = null;

// ============================================
// COMPUTED
// ============================================

export const progress = computed(() => {
  if (duration.value === 0) return 0;
  return (position.value / duration.value) * 100;
});

export const isPreviewMode = computed(() => {
  return currentProvider.value === 'preview';
});

export const currentTrackId = computed(() => {
  const track = currentTrack.value;
  if (!track) return null;

  // SampleSong
  if ('spotify_id' in track) return track.spotify_id;
  // Song (our app type)
  if ('spotifyId' in track) return track.spotifyId || (track as Song).trackId;
  // SpotifyTrack
  if ('external_urls' in track && 'id' in track) return (track as SpotifyTrack).id;
  // AppleMusicTrack
  if ('type' in track && (track as AppleMusicTrack).type === 'songs') return (track as AppleMusicTrack).id;
  // UnifiedTrack
  if ('provider' in track) return (track as UnifiedTrack).id;

  return null;
});

// ============================================
// SPOTIFY SDK
// ============================================

/**
 * Initialize Spotify Web Playback SDK
 */
export async function initSpotifySDK(): Promise<void> {
  if (spotifySdkLoaded.value) return;

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    document.body.appendChild(script);

    window.onSpotifyWebPlaybackSDKReady = () => {
      spotifySdkLoaded.value = true;
      resolve();
    };
  });
}

/**
 * Create Spotify player instance
 */
export async function createSpotifyPlayer(): Promise<void> {
  const spotifyConn = providerConnections.value.get('spotify');
  if (!spotifySdkLoaded.value || !spotifyConn?.accessToken) {
    console.log('Spotify SDK not ready or no token');
    return;
  }

  const player = new (window as any).Spotify.Player({
    name: 'Sherit Web Player',
    getOAuthToken: (cb: (token: string) => void) => {
      const conn = providerConnections.value.get('spotify');
      cb(conn?.accessToken || '');
    },
    volume: volume.value,
  });

  player.addListener('initialization_error', ({ message }: { message: string }) => {
    console.error('Spotify init error:', message);
  });

  player.addListener('authentication_error', ({ message }: { message: string }) => {
    console.error('Spotify auth error:', message);
  });

  player.addListener('account_error', ({ message }: { message: string }) => {
    console.error('Spotify account error (likely not premium):', message);
  });

  player.addListener('ready', ({ device_id }: { device_id: string }) => {
    console.log('Spotify player ready:', device_id);
    spotifyDeviceId.value = device_id;
    isReady.value = true;
  });

  player.addListener('not_ready', ({ device_id }: { device_id: string }) => {
    console.log('Spotify player not ready:', device_id);
    spotifyDeviceId.value = null;
  });

  player.addListener('player_state_changed', (state: any) => {
    if (!state) return;

    isPlaying.value = !state.paused;
    position.value = state.position;
    duration.value = state.duration;

    if (state.track_window.current_track) {
      currentTrack.value = {
        id: state.track_window.current_track.id,
        name: state.track_window.current_track.name,
        artists: state.track_window.current_track.artists,
        album: {
          id: state.track_window.current_track.album.uri.split(':')[2],
          name: state.track_window.current_track.album.name,
          images: state.track_window.current_track.album.images,
        },
        duration_ms: state.duration,
        preview_url: null,
        external_urls: { spotify: '' },
      } as SpotifyTrack;
      currentProvider.value = 'spotify';
    }
  });

  spotifyPlayer = player;
  await player.connect();
}

// ============================================
// APPLE MUSICKIT
// ============================================

/**
 * Initialize Apple MusicKit JS
 */
export async function initAppleMusicKit(developerToken: string): Promise<void> {
  if (appleMusicReady.value) return;

  // Load MusicKit JS script if not loaded
  if (typeof window.MusicKit === 'undefined') {
    await new Promise<void>((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
      script.async = true;
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }

  musicKit = await window.MusicKit.configure({
    developerToken,
    app: { name: 'Sherit', build: '1.0.0' },
  });

  // Set up event listeners
  musicKit.addEventListener('playbackStateDidChange', () => {
    if (!musicKit) return;
    isPlaying.value = musicKit.isPlaying;
  });

  musicKit.addEventListener('playbackTimeDidChange', () => {
    if (!musicKit) return;
    position.value = musicKit.currentPlaybackTime * 1000;
    duration.value = musicKit.currentPlaybackDuration * 1000;
  });

  musicKit.addEventListener('nowPlayingItemDidChange', () => {
    if (!musicKit?.nowPlayingItem) return;
    currentTrack.value = musicKit.nowPlayingItem;
    currentProvider.value = 'apple_music';
  });

  appleMusicReady.value = true;
  isReady.value = true;
}

// ============================================
// UNIFIED PLAYBACK
// ============================================

// Type alias for all playable track types
type PlayableTrack = SpotifyTrack | AppleMusicTrack | Song | SampleSong | UnifiedTrack;

/**
 * Play a track - automatically picks the best provider
 */
export async function play(track: PlayableTrack): Promise<void> {
  // Determine what provider to use
  const provider = determinePlaybackProvider(track);

  switch (provider) {
    case 'spotify':
      await playWithSpotify(track);
      break;
    case 'apple_music':
      await playWithAppleMusic(track);
      break;
    default:
      await playWithPreview(track);
  }
}

/**
 * Determine best provider for playback
 */
function determinePlaybackProvider(track: unknown): MusicProvider | 'preview' {
  // Check if we have Spotify SDK ready
  if (spotifyDeviceId.value && providerConnections.value.get('spotify')?.connected) {
    // If track has a Spotify ID, use Spotify
    if (hasSpotifyId(track)) {
      return 'spotify';
    }
  }

  // Check if Apple Music is ready
  if (appleMusicReady.value && providerConnections.value.get('apple_music')?.connected) {
    // If track has Apple Music ID, use MusicKit
    if (hasAppleMusicId(track)) {
      return 'apple_music';
    }
  }

  // Fall back to preview
  return 'preview';
}

function hasSpotifyId(track: unknown): boolean {
  if (!track || typeof track !== 'object') return false;
  return 'spotify_id' in track || 'spotifyId' in track ||
    ('id' in track && 'external_urls' in track);
}

function hasAppleMusicId(track: unknown): boolean {
  if (!track || typeof track !== 'object') return false;
  return 'appleMusicId' in track ||
    ('type' in track && (track as { type: string }).type === 'songs');
}

/**
 * Play with Spotify Web Playback SDK
 */
async function playWithSpotify(track: PlayableTrack): Promise<void> {
  const conn = providerConnections.value.get('spotify');
  if (!conn?.accessToken || !spotifyDeviceId.value) {
    console.log('Spotify not available, falling back to preview');
    await playWithPreview(track);
    return;
  }

  // Get Spotify track ID
  let trackId: string | null = null;
  if ('spotify_id' in track) trackId = track.spotify_id;
  else if ('spotifyId' in track && track.spotifyId) trackId = track.spotifyId;
  else if ('id' in track && !('type' in track)) trackId = track.id;

  if (!trackId) {
    console.log('No Spotify ID, falling back to preview');
    await playWithPreview(track);
    return;
  }

  try {
    await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${spotifyDeviceId.value}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${conn.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uris: [`spotify:track:${trackId}`],
      }),
    });

    currentTrack.value = track;
    currentProvider.value = 'spotify';
    isPlaying.value = true;
  } catch (e) {
    console.error('Spotify playback failed:', e);
    await playWithPreview(track);
  }
}

/**
 * Play with Apple MusicKit
 */
async function playWithAppleMusic(track: PlayableTrack): Promise<void> {
  if (!musicKit || !appleMusicReady.value) {
    console.log('MusicKit not available, falling back to preview');
    await playWithPreview(track);
    return;
  }

  // Get Apple Music ID
  let trackId: string | null = null;
  if ('type' in track && track.type === 'songs') trackId = track.id;
  else if ('appleMusicId' in track && track.appleMusicId) trackId = track.appleMusicId;

  if (!trackId) {
    console.log('No Apple Music ID, falling back to preview');
    await playWithPreview(track);
    return;
  }

  try {
    await musicKit.setQueue({ song: trackId });
    await musicKit.play();
    currentTrack.value = track;
    currentProvider.value = 'apple_music';
    isPlaying.value = true;
  } catch (e) {
    console.error('Apple Music playback failed:', e);
    await playWithPreview(track);
  }
}

/**
 * Play preview audio (30-second clip)
 */
async function playWithPreview(track: PlayableTrack): Promise<void> {
  // Get preview URL
  let previewUrl: string | null = null;

  if ('previewUrl' in track) previewUrl = track.previewUrl;
  else if ('preview_url' in track) previewUrl = track.preview_url;
  else if ('attributes' in track && track.attributes.previews?.[0]) {
    previewUrl = track.attributes.previews[0].url;
  }

  if (!previewUrl) {
    // No preview available - try to open in provider's app
    openInProvider(track);
    return;
  }

  // Stop current preview
  if (previewAudio) {
    previewAudio.pause();
    previewAudio.src = '';
  }

  previewAudio = new Audio(previewUrl);
  previewAudio.volume = volume.value;

  previewAudio.onplay = () => {
    isPlaying.value = true;
    currentTrack.value = track;
    currentProvider.value = 'preview';
    startPositionTracking();
  };

  previewAudio.onpause = () => {
    isPlaying.value = false;
    stopPositionTracking();
  };

  previewAudio.onended = () => {
    isPlaying.value = false;
    position.value = 0;
    stopPositionTracking();
  };

  previewAudio.ontimeupdate = () => {
    position.value = previewAudio!.currentTime * 1000;
    duration.value = previewAudio!.duration * 1000;
  };

  try {
    await previewAudio.play();
  } catch (e) {
    console.error('Preview playback failed:', e);
  }
}

/**
 * Open track in provider's app/website
 */
function openInProvider(track: unknown): void {
  if (!track || typeof track !== 'object') return;

  // Spotify
  if ('spotify_id' in track) {
    window.open(`https://open.spotify.com/track/${(track as { spotify_id: string }).spotify_id}`, '_blank');
    return;
  }
  if ('spotifyId' in track && (track as { spotifyId?: string }).spotifyId) {
    window.open(`https://open.spotify.com/track/${(track as { spotifyId: string }).spotifyId}`, '_blank');
    return;
  }
  if ('external_urls' in track) {
    const urls = (track as { external_urls: { spotify?: string } }).external_urls;
    if (urls.spotify) {
      window.open(urls.spotify, '_blank');
      return;
    }
  }

  // Apple Music
  if ('attributes' in track) {
    const attrs = (track as AppleMusicTrack).attributes;
    if (attrs.url) {
      window.open(attrs.url, '_blank');
      return;
    }
  }
  if ('externalUrl' in track && (track as { externalUrl?: string }).externalUrl) {
    window.open((track as { externalUrl: string }).externalUrl, '_blank');
  }
}

// ============================================
// PLAYBACK CONTROLS
// ============================================

/**
 * Pause playback
 */
export async function pause(): Promise<void> {
  if (currentProvider.value === 'spotify' && spotifyPlayer) {
    await spotifyPlayer.pause();
  } else if (currentProvider.value === 'apple_music' && musicKit) {
    await musicKit.pause();
  } else if (previewAudio) {
    previewAudio.pause();
  }

  isPlaying.value = false;
}

/**
 * Toggle play/pause
 */
export async function togglePlay(): Promise<void> {
  if (isPlaying.value) {
    await pause();
  } else {
    if (currentProvider.value === 'spotify' && spotifyPlayer) {
      await spotifyPlayer.resume();
    } else if (currentProvider.value === 'apple_music' && musicKit) {
      await musicKit.play();
    } else if (previewAudio) {
      await previewAudio.play();
    }
    isPlaying.value = true;
  }
}

/**
 * Seek to position (ms)
 */
export async function seek(positionMs: number): Promise<void> {
  if (currentProvider.value === 'spotify' && spotifyPlayer) {
    await spotifyPlayer.seek(positionMs);
  } else if (currentProvider.value === 'apple_music' && musicKit) {
    await musicKit.seekToTime(positionMs / 1000);
  } else if (previewAudio) {
    previewAudio.currentTime = positionMs / 1000;
  }

  position.value = positionMs;
}

/**
 * Set volume (0-1)
 */
export async function setVolume(newVolume: number): Promise<void> {
  volume.value = Math.max(0, Math.min(1, newVolume));

  if (spotifyPlayer) {
    await spotifyPlayer.setVolume(volume.value);
  }
  if (musicKit) {
    musicKit.volume = volume.value;
  }
  if (previewAudio) {
    previewAudio.volume = volume.value;
  }
}

/**
 * Skip to next track
 */
export async function next(): Promise<void> {
  if (currentProvider.value === 'spotify' && spotifyPlayer) {
    await spotifyPlayer.nextTrack();
  }
  // MusicKit and preview don't have queue support in this implementation
}

/**
 * Skip to previous track
 */
export async function previous(): Promise<void> {
  if (currentProvider.value === 'spotify' && spotifyPlayer) {
    await spotifyPlayer.previousTrack();
  }
}

// ============================================
// POSITION TRACKING
// ============================================

function startPositionTracking(): void {
  if (positionInterval) return;
  positionInterval = window.setInterval(() => {
    if (previewAudio && isPlaying.value) {
      position.value = previewAudio.currentTime * 1000;
    }
  }, 250);
}

function stopPositionTracking(): void {
  if (positionInterval) {
    clearInterval(positionInterval);
    positionInterval = null;
  }
}

// ============================================
// CLEANUP
// ============================================

/**
 * Disconnect all players
 */
export function disconnect(): void {
  if (spotifyPlayer) {
    spotifyPlayer.disconnect();
    spotifyPlayer = null;
  }

  if (musicKit) {
    musicKit.stop().catch(console.error);
  }

  if (previewAudio) {
    previewAudio.pause();
    previewAudio.src = '';
    previewAudio = null;
  }

  stopPositionTracking();

  isReady.value = false;
  spotifyDeviceId.value = null;
  appleMusicReady.value = false;
  isPlaying.value = false;
  currentTrack.value = null;
  currentProvider.value = null;
}

// ============================================
// GLOBAL TYPE DECLARATIONS
// ============================================

declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady: () => void;
    Spotify: any;
  }
}
